// src/admin/pages/category/AddCategoryDrawer.tsx
//
// Standalone "Add New Category / Subcategory" drawer — same fields and
// validation as the add-mode drawer in CategoryManagement.tsx (a single
// form drives both — a subcategory is just a category with parentId set),
// extracted so it can also be launched from ProductDrawer.tsx (quick-add
// while creating a product).
import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  TextField,
  Grid,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { CategoryService } from './categoryService';
import { Category } from '../../../enums/product';
import { useAuth } from '../../../contexts/AuthContext';

interface AddCategoryDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Main categories to populate the "Parent Category" dropdown. */
  parentCategories: Category[];
  /** Pre-selects a parent (e.g. launched via "Add Subcategory"); still editable, matching CategoryManagement's own behaviour. */
  defaultParentId?: string;
  onCreated: (categoryId: string, parentId: string) => void;
  onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const AddCategoryDrawer: React.FC<AddCategoryDrawerProps> = ({
  open, onClose, parentCategories, defaultParentId, onCreated, onShowToast,
}) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({ name: '', description: '', parentId: '' });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({ name: '', description: '', parentId: defaultParentId || '' });
      setImageFile(null);
      setImagePreview('');
    }
  }, [open, defaultParentId]);

  // Image upload is only for main categories, matching CategoryManagement.
  const shouldShowImageUpload = !formData.parentId;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Switching to a parent (i.e. becoming a subcategory) clears any chosen
    // image, since subcategories don't support images — same as the original.
    if (name === 'parentId' && value) {
      setImageFile(null);
      setImagePreview('');
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { onShowToast('Please select an image file', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { onShowToast('Image size should be less than 5MB', 'error'); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => { setImageFile(null); setImagePreview(''); };

  const handleClose = () => { if (saveLoading) return; onClose(); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { onShowToast('Category name is required', 'error'); return; }

    // Image is mandatory for main categories, never required for subcategories.
    if (!formData.parentId && !imageFile) {
      onShowToast('Category image is required for main categories', 'error');
      return;
    }

    setSaveLoading(true);
    try {
      const newId = await CategoryService.createCategory(
        { name: formData.name, description: formData.description, parentId: formData.parentId, isActive: true, sortOrder: '0' },
        currentUser?.uid || '',
        imageFile
      );
      onShowToast(formData.parentId ? 'Subcategory created successfully' : 'Category created successfully', 'success');
      onCreated(newId, formData.parentId);
      onClose();
    } catch (err: any) {
      onShowToast(`Failed to save category: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={handleClose} PaperProps={{ sx: { width: { xs: '100%', sm: 500 } } }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Typography variant="h6">{formData.parentId ? 'Add New Subcategory' : 'Add New Category'}</Typography>
            <IconButton onClick={handleClose} edge="end"><CloseIcon /></IconButton>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Category Name" name="name" value={formData.name}
                    onChange={handleInputChange} required placeholder="Enter category name" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Description" name="description" value={formData.description}
                    onChange={handleInputChange} multiline rows={3} placeholder="Enter category description" />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Parent Category</InputLabel>
                    <Select name="parentId" value={formData.parentId} onChange={handleSelectChange} label="Parent Category">
                      <MenuItem value="">None (Main Category)</MenuItem>
                      {parentCategories.map(category => (
                        <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {shouldShowImageUpload && (
                  <Grid item xs={12}>
                    <Box sx={{ border: '1px dashed', borderColor: !imagePreview && !imageFile ? 'error.main' : 'divider', borderRadius: 1, p: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle2">Category Image</Typography>
                        <Typography variant="subtitle2" color="error">*</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Required. Upload an image for this main category (max 5 MB).
                      </Typography>
                      {imagePreview ? (
                        <Box>
                          <Box component="img" src={imagePreview} alt="Category preview"
                            sx={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1, mb: 1 }} />
                          <Button onClick={handleRemoveImage} variant="outlined" color="error" size="small"
                            startIcon={<DeleteIcon />} fullWidth>
                            Remove Image
                          </Button>
                        </Box>
                      ) : (
                        <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth color="error">
                          Upload Image
                          <input type="file" hidden accept="image/*" onChange={handleImageSelect} />
                        </Button>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
            <Divider />
            <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
              <Button onClick={handleClose} disabled={saveLoading} startIcon={<CancelIcon />}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={saveLoading}
                startIcon={saveLoading ? <CircularProgress size={16} /> : <SaveIcon />}>
                {saveLoading ? 'Creating...' : formData.parentId ? 'Create Subcategory' : 'Create Category'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Backdrop
        open={saveLoading}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, px: 5, py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, minWidth: 280, boxShadow: 24 }}>
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              {formData.parentId ? 'Creating Subcategory…' : 'Creating Category…'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
    </>
  );
};

export default AddCategoryDrawer;
